// src/screens/DealerStaffScreen.js
// Dealer-admin screen to invite, deactivate and reactivate their dealership's
// staff. Rendered as a tab inside DealerDashboardScreen. Dealer admins manage
// staff only; kiosks and other dealer admins are managed by SelfDrive.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { AuthContext } from '../context/AuthContext';
import { dealerAPI } from '../services/api';

const inputClass =
  'mt-1 w-full px-3 py-2 rounded-xl bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border text-sm text-app-text-light dark:text-app-text outline-none focus:border-app-accent';

const DealerStaffScreen = () => {
  const { user } = useContext(AuthContext);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [inviting, setInviting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await dealerAPI.listStaff();
      setStaff(data);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load staff');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (message) => {
    setError('');
    setSuccess(message);
    setTimeout(() => setSuccess(''), 4000);
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      setError('Enter a name and an email address.');
      return;
    }
    setInviting(true);
    setError('');
    try {
      await dealerAPI.inviteStaff(name.trim(), email.trim());
      flash(`Invitation sent to ${email.trim()}. They will choose their own password.`);
      setName('');
      setEmail('');
      await load();
    } catch (e2) {
      setError(e2.response?.data?.message || 'Failed to send the invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleToggle = async (member) => {
    const deactivate = member.active !== false;
    if (deactivate && !window.confirm(`Remove access for ${member.name}? They will be signed out on every device immediately.`)) {
      return;
    }
    setBusyId(member._id);
    try {
      await dealerAPI.setStaffActive(member._id, !deactivate);
      flash(deactivate ? `${member.name} no longer has access.` : `${member.name} has access again.`);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to update access');
    } finally {
      setBusyId(null);
    }
  };

  const handleResend = async (member) => {
    setBusyId(member._id);
    try {
      await dealerAPI.resendStaffInvite(member._id);
      flash(`A new set-password link was sent to ${member.email}.`);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to send the link');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4 max-w-3xl">
      <div>
        <h2 className="text-lg font-semibold text-app-text-light dark:text-app-text">Staff</h2>
        <p className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">
          Invite your team and remove access when someone leaves. Removing access signs them out everywhere at once.
        </p>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3">
          <p className="text-red-400 text-xs">{error}</p>
        </div>
      )}
      {success && (
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3">
          <p className="text-green-500 text-xs">{success}</p>
        </div>
      )}

      <form
        onSubmit={handleInvite}
        className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-4 border border-app-border-light dark:border-app-border space-y-3"
      >
        <div className="text-sm font-semibold text-app-text-light dark:text-app-text">Invite a staff member</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">
            Name
            <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Smith" />
          </label>
          <label className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">
            Email
            <input type="email" className={inputClass} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@yourdealership.com" />
          </label>
        </div>
        <button
          type="submit"
          disabled={inviting}
          className="px-4 py-2 rounded-xl bg-app-accent text-black text-sm font-semibold hover:opacity-90 disabled:opacity-50"
        >
          {inviting ? 'Sending…' : 'Send invitation'}
        </button>
      </form>

      <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl border border-app-border-light dark:border-app-border divide-y divide-app-border-light dark:divide-app-border">
        {loading && <div className="p-4 text-xs">Loading…</div>}
        {!loading && staff.length === 0 && <div className="p-4 text-xs">No staff yet.</div>}
        {staff.map((member) => {
          const isSelf = String(member._id) === String(user?._id || user?.id);
          const isStaff = member.role === 'dealer_staff';
          const active = member.active !== false;
          return (
            <div key={member._id} className="p-4 flex flex-wrap items-center gap-3 justify-between">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-app-text-light dark:text-app-text truncate">
                  {member.name} {isSelf && <span className="text-xs font-normal">(you)</span>}
                </div>
                <div className="text-xs text-app-text-secondary-light dark:text-app-text-secondary truncate">{member.email}</div>
                <div className="text-[11px] mt-1">
                  <span className="px-2 py-0.5 rounded-full bg-app-bg-light dark:bg-app-bg">
                    {isStaff ? 'Staff' : 'Admin'}
                  </span>{' '}
                  {!active && <span className="px-2 py-0.5 rounded-full bg-red-500/15 text-red-500">No access</span>}
                </div>
              </div>
              {isStaff && (
                <div className="flex gap-2">
                  {active && (
                    <button
                      onClick={() => handleResend(member)}
                      disabled={busyId === member._id}
                      className="px-3 py-2 rounded-xl text-xs font-semibold bg-app-bg-light dark:bg-app-bg disabled:opacity-50"
                    >
                      Resend link
                    </button>
                  )}
                  <button
                    onClick={() => handleToggle(member)}
                    disabled={busyId === member._id}
                    className={`px-3 py-2 rounded-xl text-xs font-semibold disabled:opacity-50 ${
                      active ? 'bg-red-500/15 text-red-500' : 'bg-app-accent text-black'
                    }`}
                  >
                    {active ? 'Remove access' : 'Restore access'}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DealerStaffScreen;
